import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, ScrollText, Loader2, ShieldCheck, FileArchive } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api, API, getSigningInfo } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const EMPTY = {
  verbale_num: "", tribunale: "", sezione: "", materia: "CIVILE", giudice: "",
  rg_num: "", rg_year: "",
  data_accesso: "", ora_inizio: "",
  luogo_presso: "", luogo_indirizzo: "", luogo_citta: "", luogo_provincia: "",
  presenti: "", ctp_nominati: null,
  udienza_data: "", quesito: "",
  attivita: { acquisizione: false, osservazione: false, analisi_strumentale: false, saggio_grafico: false, altro: false },
  attivita_altro: "",
  elenco_documenti: "", altre_attivita: "", dichiarazioni: "",
  termini_udienza_data: "", termini: "",
  ora_chiusura: "", data_chiusura: "",
};

export default function VerbaleAccesso() {
  const { isAdmin, loading } = useAuth();
  const [f, setF] = useState(EMPTY);
  const [savedId, setSavedId] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [signing, setSigning] = useState({ pades: false, cades: false });
  const [signerInfo, setSignerInfo] = useState(null);

  useEffect(() => {
    getSigningInfo().then(setSignerInfo).catch(() => {});
  }, []);

  const set = (patch) => setF((s) => ({ ...s, ...patch }));
  const setAct = (k, v) => setF((s) => ({ ...s, attivita: { ...s.attivita, [k]: v } }));

  const save = async (e) => {
    e?.preventDefault();
    try {
      const r = await api.post("/verbale-accesso", f);
      setSavedId(r.data.id);
      toast.success("Verbale salvato");
      return r.data.id;
    } catch {
      toast.error("Errore nel salvataggio");
      return null;
    }
  };

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      const res = await fetch(`${API}/verbale-accesso/preview-pdf`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(f),
      });
      if (res.status === 401 || res.status === 403) {
        toast.error("Accedi come amministratore per generare il PDF");
        return;
      }
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `verbale-accesso-${f.verbale_num || "bozza"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Errore download PDF");
    } finally {
      setPdfLoading(false);
    }
  };

  const downloadSigned = async (kind) => {
    let id = savedId;
    if (!id) {
      id = await save();
      if (!id) return;
    }
    setSigning((s) => ({ ...s, [kind]: true }));
    try {
      const endpoint = kind === "pades" ? "signed-pdf" : "signed-p7m";
      const res = await fetch(`${API}/verbale-accesso/${id}/${endpoint}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: kind === "pades" ? JSON.stringify({}) : undefined,
      });
      if (res.status === 401 || res.status === 403) { toast.error("Accedi come amministratore per firmare digitalmente"); return; }
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = kind === "pades"
        ? `verbale-firmato-${f.verbale_num || id.slice(0, 8)}.pdf`
        : `verbale-${f.verbale_num || id.slice(0, 8)}.pdf.p7m`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(kind === "pades" ? "PDF firmato (PAdES) scaricato" : "Busta CAdES (.p7m) scaricata");
    } catch {
      toast.error("Errore durante la firma");
    } finally {
      setSigning((s) => ({ ...s, [kind]: false }));
    }
  };

  return (
    <div className="min-h-screen bg-paper flex flex-col">
      <Header />
      {!loading && !isAdmin && (
        <main className="flex-1 flex items-center justify-center px-6" data-testid="verbale-forbidden">
          <div className="text-center max-w-md">
            <ShieldCheck className="text-slate-300 mx-auto" size={40} />
            <h1 className="font-serif text-2xl font-semibold mt-4">Strumento riservato</h1>
            <p className="text-sm text-slate-600 mt-2">Il Verbale di accesso peritale è disponibile solo agli amministratori.</p>
            <Link to="/" className="inline-block mt-4 text-xs border hairline hover:border-navy px-4 py-2 rounded-sm">Torna alla home</Link>
          </div>
        </main>
      )}
      {isAdmin && (
      <main className="flex-1 max-w-6xl mx-auto px-6 py-10 w-full">
        <div className="overline mb-2 flex items-center gap-2"><ScrollText size={14} /> Verbale di accesso peritale</div>
        <h1 className="font-serif text-3xl lg:text-4xl font-semibold tracking-tight">Verbale di accesso peritale</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">
          Compila i campi essenziali dell'accesso peritale. Il sistema genera il PDF
          seguendo il modello ufficiale (intestazione, tribunale, quesito, attività,
          elenco documenti, dichiarazioni, termini).
        </p>

        <form onSubmit={save} className="border hairline bg-white p-6 lg:p-10 mt-8 space-y-8">
          {/* Intestazione */}
          <section>
            <div className="overline mb-4">Intestazione</div>
            <div className="grid md:grid-cols-4 gap-4">
              <Field label="Verbale N°"><In value={f.verbale_num} onChange={(v) => set({ verbale_num: v })} testid="va-num" /></Field>
              <Field label="R.G. N°"><In value={f.rg_num} onChange={(v) => set({ rg_num: v })} testid="va-rg-num" /></Field>
              <Field label="Anno R.G."><In value={f.rg_year} onChange={(v) => set({ rg_year: v })} testid="va-rg-year" /></Field>
              <Field label="Materia">
                <select value={f.materia} onChange={(e) => set({ materia: e.target.value })} className="w-full border hairline px-3 py-2 rounded-sm bg-white" data-testid="va-materia">
                  <option>CIVILE</option>
                  <option>PENALE</option>
                  <option>LAVORO</option>
                </select>
              </Field>
              <Field label="Tribunale di"><In value={f.tribunale} onChange={(v) => set({ tribunale: v })} testid="va-tribunale" /></Field>
              <Field label="Sezione"><In value={f.sezione} onChange={(v) => set({ sezione: v })} testid="va-sezione" /></Field>
              <div className="md:col-span-2">
                <Field label="Giudice"><In value={f.giudice} onChange={(v) => set({ giudice: v })} testid="va-giudice" /></Field>
              </div>
            </div>
          </section>

          {/* Data e luogo */}
          <section>
            <div className="overline mb-4">Data e luogo dell'accesso</div>
            <div className="grid md:grid-cols-4 gap-4">
              <Field label="Data accesso"><In type="date" value={f.data_accesso} onChange={(v) => set({ data_accesso: v })} testid="va-data" /></Field>
              <Field label="Ora inizio"><In type="time" value={f.ora_inizio} onChange={(v) => set({ ora_inizio: v })} testid="va-ora" /></Field>
              <div className="md:col-span-2">
                <Field label="Presso (studio, tribunale, ecc.)"><In value={f.luogo_presso} onChange={(v) => set({ luogo_presso: v })} testid="va-presso" /></Field>
              </div>
              <div className="md:col-span-2">
                <Field label="Indirizzo"><In value={f.luogo_indirizzo} onChange={(v) => set({ luogo_indirizzo: v })} testid="va-indirizzo" /></Field>
              </div>
              <Field label="Città"><In value={f.luogo_citta} onChange={(v) => set({ luogo_citta: v })} testid="va-citta" /></Field>
              <Field label="Provincia"><In value={f.luogo_provincia} onChange={(v) => set({ luogo_provincia: v })} testid="va-provincia" /></Field>
            </div>
          </section>

          {/* Presenti */}
          <section>
            <div className="overline mb-4">Presenti e CTP</div>
            <Field label="Elenco dei presenti (una persona per riga)">
              <textarea rows={4} value={f.presenti} onChange={(e) => set({ presenti: e.target.value })}
                placeholder="Es. Avv. Mario Rossi (parte attrice)&#10;Avv. Luca Bianchi (parte convenuta)"
                className="w-full border hairline px-3 py-2 rounded-sm bg-white" data-testid="va-presenti" />
            </Field>
            <div className="mt-3">
              <div className="overline mb-2">Le parti nominano Consulenti di Parte?</div>
              <div className="flex gap-2">
                {[[true, "Sì"], [false, "No"], [null, "—"]].map(([v, l]) => (
                  <button key={String(v)} type="button" onClick={() => set({ ctp_nominati: v })}
                    className={`px-4 py-2 rounded-sm text-sm border hairline transition-colors ${f.ctp_nominati === v ? "bg-navy text-white border-navy" : "bg-white hover:bg-slate-100"}`}
                    data-testid={`va-ctp-${String(v)}`}>{l}</button>
                ))}
              </div>
            </div>
          </section>

          {/* Quesito */}
          <section>
            <div className="overline mb-4">Quesito del Giudice</div>
            <div className="grid md:grid-cols-4 gap-4">
              <Field label="Data udienza"><In type="date" value={f.udienza_data} onChange={(v) => set({ udienza_data: v })} testid="va-udienza-data" /></Field>
              <div className="md:col-span-4">
                <Field label="Testo del quesito">
                  <textarea rows={5} value={f.quesito} onChange={(e) => set({ quesito: e.target.value })}
                    className="w-full border hairline px-3 py-2 rounded-sm bg-white" data-testid="va-quesito" />
                </Field>
              </div>
            </div>
          </section>

          {/* Attività */}
          <section>
            <div className="overline mb-4">Attività svolte</div>
            <div className="grid md:grid-cols-2 gap-3">
              <Check label="Acquisizione documenti" checked={f.attivita.acquisizione} onChange={(v) => setAct("acquisizione", v)} testid="va-att-acquisizione" />
              <Check label="Osservazione" checked={f.attivita.osservazione} onChange={(v) => setAct("osservazione", v)} testid="va-att-osservazione" />
              <Check label="Analisi strumentale VIS – IR – UV" checked={f.attivita.analisi_strumentale} onChange={(v) => setAct("analisi_strumentale", v)} testid="va-att-analisi" />
              <Check label="Saggio grafico" checked={f.attivita.saggio_grafico} onChange={(v) => setAct("saggio_grafico", v)} testid="va-att-saggio" />
              <Check label="Altro" checked={f.attivita.altro} onChange={(v) => setAct("altro", v)} testid="va-att-altro" />
              {f.attivita.altro && (
                <div className="md:col-span-2">
                  <In value={f.attivita_altro} onChange={(v) => set({ attivita_altro: v })} placeholder="Specifica..." testid="va-att-altro-txt" />
                </div>
              )}
            </div>

            <div className="mt-4">
              <Field label="Elenco documenti">
                <textarea rows={4} value={f.elenco_documenti} onChange={(e) => set({ elenco_documenti: e.target.value })}
                  placeholder="Un documento per riga..." className="w-full border hairline px-3 py-2 rounded-sm bg-white" data-testid="va-elenco" />
              </Field>
            </div>
            <div className="mt-4">
              <Field label="Altre attività">
                <textarea rows={3} value={f.altre_attivita} onChange={(e) => set({ altre_attivita: e.target.value })}
                  className="w-full border hairline px-3 py-2 rounded-sm bg-white" data-testid="va-altre" />
              </Field>
            </div>
          </section>

          {/* Dichiarazioni e termini */}
          <section>
            <div className="overline mb-4">Dichiarazioni e termini</div>
            <Field label="Eventuali dichiarazioni delle parti">
              <textarea rows={4} value={f.dichiarazioni} onChange={(e) => set({ dichiarazioni: e.target.value })}
                className="w-full border hairline px-3 py-2 rounded-sm bg-white" data-testid="va-dichiarazioni" />
            </Field>
            <div className="grid md:grid-cols-4 gap-4 mt-4">
              <Field label="Data udienza termini"><In type="date" value={f.termini_udienza_data} onChange={(v) => set({ termini_udienza_data: v })} testid="va-termini-data" /></Field>
              <div className="md:col-span-3">
                <Field label="Termini assegnati dal Giudice">
                  <textarea rows={3} value={f.termini} onChange={(e) => set({ termini: e.target.value })}
                    className="w-full border hairline px-3 py-2 rounded-sm bg-white" data-testid="va-termini" />
                </Field>
              </div>
            </div>
          </section>

          {/* Chiusura */}
          <section>
            <div className="overline mb-4">Chiusura</div>
            <div className="grid md:grid-cols-4 gap-4">
              <Field label="Ora chiusura"><In type="time" value={f.ora_chiusura} onChange={(v) => set({ ora_chiusura: v })} testid="va-ora-chiusura" /></Field>
              <Field label="Data chiusura"><In type="date" value={f.data_chiusura} onChange={(v) => set({ data_chiusura: v })} testid="va-data-chiusura" /></Field>
            </div>
          </section>

          <div className="flex flex-wrap items-center justify-end gap-3 pt-4 border-t hairline">
            <button type="submit" className="inline-flex items-center gap-2 border border-navy text-navy hover:bg-navy hover:text-white px-6 py-2 rounded-sm text-sm font-medium transition-colors" data-testid="va-save-btn">
              Salva su fascicolo
            </button>
            <button type="button" onClick={downloadPdf} disabled={pdfLoading}
              className="inline-flex items-center gap-2 bg-navy hover:bg-navy-dark text-white px-6 py-2 rounded-sm text-sm font-medium transition-colors disabled:opacity-60"
              data-testid="va-download-btn">
              {pdfLoading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              Scarica PDF
            </button>
          </div>

          {/* Firma digitale */}
          <div className="border-t hairline pt-6 mt-2" data-testid="va-signing-section">
            <div className="overline mb-2 flex items-center gap-2"><ShieldCheck size={14} /> Firma digitale</div>
            {signerInfo && (
              <div className={`text-xs p-3 rounded-sm border mb-4 ${signerInfo.is_test_certificate ? "border-forensic-amber/30 bg-forensic-amber/10 text-forensic-amber" : "border-forensic-green/30 bg-forensic-green/10 text-forensic-green"}`} data-testid="va-signer-info">
                <div className="font-mono uppercase tracking-wider">
                  {signerInfo.is_test_certificate ? "Certificato di test" : "Certificato caricato"}
                </div>
                <div className="mt-1 text-slate-700">Firmatario: <span className="font-mono">{signerInfo.signer_common_name}</span></div>
                <div className="mt-1 text-slate-700">{signerInfo.notice}</div>
              </div>
            )}
            <p className="text-sm text-slate-600 mb-4 max-w-3xl">
              Scarica il verbale già firmato digitalmente. <b>PAdES</b> incorpora la firma
              all'interno del PDF (rimane leggibile con qualsiasi lettore). <b>CAdES</b> genera
              una busta crittografica <span className="font-mono">.p7m</span> che contiene documento e firma
              (formato tradizionalmente accettato nei depositi telematici).
            </p>
            <div className="flex flex-wrap gap-3">
              <button type="button" onClick={() => downloadSigned("pades")} disabled={signing.pades}
                className="inline-flex items-center gap-2 bg-forensic-green hover:brightness-110 text-white px-5 py-2 rounded-sm text-sm font-medium transition-all disabled:opacity-60"
                data-testid="va-sign-pades-btn">
                {signing.pades ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
                Scarica PDF firmato (PAdES)
              </button>
              <button type="button" onClick={() => downloadSigned("cades")} disabled={signing.cades}
                className="inline-flex items-center gap-2 border border-navy text-navy hover:bg-navy hover:text-white px-5 py-2 rounded-sm text-sm font-medium transition-colors disabled:opacity-60"
                data-testid="va-sign-cades-btn">
                {signing.cades ? <Loader2 size={14} className="animate-spin" /> : <FileArchive size={14} />}
                Scarica busta CAdES (.p7m)
              </button>
            </div>
          </div>
          {savedId && (
            <div className="text-xs font-mono text-slate-500" data-testid="va-saved-id">
              Salvato con ID: {savedId}
            </div>
          )}
        </form>
      </main>
      )}
      <Footer />
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block"><div className="overline mb-2">{label}</div>{children}</label>;
}
function In({ value, onChange, type = "text", placeholder, testid }) {
  return <input type={type} value={value ?? ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
    className="w-full border hairline px-3 py-2 rounded-sm bg-white" data-testid={testid} />;
}
function Check({ label, checked, onChange, testid }) {
  return (
    <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="accent-navy" data-testid={testid} />
      {label}
    </label>
  );
}
